// The aetherometer (R): a brass instrument panel listing the planet's
// active named spawns; tune to one and it reads a 5×5 concentration grid
// around you with a needle toward the best cell, then lets you channel a
// hand-sample where you stand. Plain DOM like every panel.

import {
  SAMPLE_CHANNEL_MS,
  type SampleResultMessage,
  SURVEY_GRID_SIZE,
  type SurveyResultMessage,
} from "@bebop/shared";

import { escapeHtml } from "./escape";

interface ActiveSpawnInfo {
  id: string;
  name: string;
  className: string;
  branch: string;
  stats: Record<string, number>;
}

const FAIL_COPY: Record<string, string> = {
  "weak-ground": "The ground here is too thin. Follow better numbers.",
  moved: "You wandered off mid-sample.",
  expired: "That vein has gone from the world.",
  busy: "Already sampling.",
  mounted: "Dismount to sample.",
  "hold-full": "Your hold is full. Stow something in the settlement locker.",
};

export class SurveyPanel {
  private root: HTMLDivElement;
  private list: HTMLDivElement;
  private reading: HTMLDivElement;
  private open = false;
  private selected: ActiveSpawnInfo | undefined;
  private sampling = false;
  /** Watchdog: if the result never arrives (drop, portal), unlatch. */
  private sampleTimer: number | undefined;

  constructor(
    parent: HTMLElement,
    private send: {
      survey(spawnId: string): void;
      sample(spawnId: string): void;
      seal?(): void;
    },
  ) {
    this.root = document.createElement("div");
    this.root.className = "panel-float survey-panel";
    this.root.innerHTML = `
      <div class="panel-title">AETHEROMETER <span class="panel-hint">R to close</span></div>
      <div class="spawn-list"></div>
      <div class="survey-reading"></div>
    `;
    this.root.style.display = "none";
    parent.append(this.root);
    this.list = this.root.querySelector(".spawn-list") as HTMLDivElement;
    this.reading = this.root.querySelector(".survey-reading") as HTMLDivElement;
  }

  toggle(): void {
    this.open = !this.open;
    this.root.style.display = this.open ? "" : "none";
    if (this.open) void this.loadSpawns();
  }

  close(): void {
    if (this.open) this.toggle();
  }

  isOpen(): boolean {
    return this.open;
  }

  private async loadSpawns(): Promise<void> {
    this.list.innerHTML = `<div class="panel-empty">listening to the ground…</div>`;
    try {
      const res = await fetch("/api/resources/active");
      const body = (await res.json()) as { spawns: ActiveSpawnInfo[] };
      this.renderList(body.spawns);
    } catch {
      this.list.innerHTML = `<div class="panel-empty">the needle won't settle (try again)</div>`;
    }
  }

  private renderList(spawns: ActiveSpawnInfo[]): void {
    this.list.innerHTML = "";
    if (spawns.length === 0) {
      this.list.innerHTML = `<div class="panel-empty">nothing in the ground today</div>`;
      return;
    }
    for (const spawn of spawns) {
      const row = document.createElement("button");
      row.className = "spawn-row";
      row.innerHTML = `
        <span class="spawn-name">${escapeHtml(spawn.name)}</span>
        <span class="spawn-class">${escapeHtml(spawn.className)}</span>
        <span class="spawn-stats">${statChips(spawn.stats)}</span>
      `;
      row.addEventListener("click", () => {
        this.selected = spawn;
        for (const el of this.list.querySelectorAll(".spawn-row")) el.classList.remove("selected");
        row.classList.add("selected");
        this.reading.innerHTML = `<div class="panel-empty">reading ${spawn.name}…</div>`;
        this.send.survey(spawn.id);
      });
      this.list.append(row);
    }
  }

  showSurvey(result: SurveyResultMessage): void {
    if (!this.open) return;
    const size = SURVEY_GRID_SIZE;
    let cells = "";
    for (let i = 0; i < result.grid.length; i++) {
      const v = result.grid[i] ?? 0;
      const alpha = 0.12 + v * 0.85;
      const isBest =
        i ===
        (result.best.dz + Math.floor(size / 2)) * size + (result.best.dx + Math.floor(size / 2));
      cells += `<div class="survey-cell${isBest ? " best" : ""}" style="background: rgba(216,176,74,${alpha.toFixed(3)})">${Math.round(v * 100)}</div>`;
    }
    // Needle: bearing toward the best cell (north = −z is up on the grid).
    const angle = (Math.atan2(result.best.dx, -result.best.dz) * 180) / Math.PI;
    this.reading.innerHTML = `
      <div class="survey-head">
        <span class="spawn-name">${escapeHtml(result.name)}</span>
        <span class="survey-needle" style="transform: rotate(${angle.toFixed(0)}deg)">▲</span>
        <span class="survey-best">${Math.round(result.best.value * 100)}% at best</span>
      </div>
      <div class="survey-grid" style="grid-template-columns: repeat(${size}, 1fr)">${cells}</div>
      <div class="survey-legend">each cell ${result.stepM} m · you are the centre</div>
      <button class="sample-btn">take a sample here</button>
      <button class="seal-btn">seal this reading</button>
      <div class="sample-progress"><div class="sample-bar"></div></div>
      <div class="sample-note"></div>
    `;
    const seal = this.reading.querySelector(".seal-btn") as HTMLButtonElement | null;
    seal?.addEventListener("click", () => {
      this.send.seal?.();
      seal.disabled = true;
      seal.textContent = "sealed";
    });
    const btn = this.reading.querySelector(".sample-btn") as HTMLButtonElement;
    btn.addEventListener("click", () => {
      if (this.sampling || !this.selected) return;
      this.sampling = true;
      btn.disabled = true;
      const bar = this.reading.querySelector(".sample-bar") as HTMLDivElement;
      bar.style.transition = "none";
      bar.style.width = "0%";
      requestAnimationFrame(() => {
        bar.style.transition = `width ${SAMPLE_CHANNEL_MS}ms linear`;
        bar.style.width = "100%";
      });
      this.send.sample(this.selected.id);
      // If the result is lost (reconnect, portal), unlatch so sampling
      // isn't dead for the rest of the session.
      this.sampleTimer = window.setTimeout(() => {
        this.showSample({ spawnId: this.selected?.id ?? "", name: "", ok: false });
        const note = this.reading.querySelector(".sample-note");
        if (note) note.textContent = "The sample was lost. Try again.";
      }, SAMPLE_CHANNEL_MS + 2500);
    });
  }

  showSample(result: SampleResultMessage): void {
    this.sampling = false;
    if (this.sampleTimer !== undefined) {
      window.clearTimeout(this.sampleTimer);
      this.sampleTimer = undefined;
    }
    if (!this.open) return;
    const note = this.reading.querySelector(".sample-note") as HTMLDivElement | null;
    const btn = this.reading.querySelector(".sample-btn") as HTMLButtonElement | null;
    const bar = this.reading.querySelector(".sample-bar") as HTMLDivElement | null;
    if (btn) btn.disabled = false;
    if (bar) {
      bar.style.transition = "none";
      bar.style.width = "0%";
    }
    if (!note) return;
    if (result.ok) {
      note.textContent = `+${result.units} units of ${result.name} (stack: ${result.stackUnits})`;
      note.className = "sample-note good";
    } else {
      note.textContent = FAIL_COPY[result.reason ?? ""] ?? "The sample came up empty.";
      note.className = "sample-note bad";
    }
  }
}

function statChips(stats: Record<string, number>): string {
  return Object.entries(stats)
    .map(([stat, value]) => `<span class="stat-chip">${abbreviate(stat)} ${value}</span>`)
    .join("");
}

function abbreviate(stat: string): string {
  const map: Record<string, string> = {
    quality: "QL",
    decay_resistance: "DR",
    malleability: "MA",
    conductivity: "CD",
    toughness: "TO",
    potency: "PO",
  };
  return map[stat] ?? stat.slice(0, 2).toUpperCase();
}

export { statChips };
